import {
  BookOpenText,
  ChevronRight,
  Edit2,
  Eye,
  FileText,
  FolderOpen,
  MoreHorizontal,
  Plus,
  Trash2,
  User,
} from 'lucide-react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import * as React from 'react';
import { BundlesCasePartnerComposer } from '@/components/bundle/BundlesCasePartnerComposer';
import { CreateBundleDrawer } from '@/components/bundle/CreateBundleDrawer';
import { DeleteAlertDialog } from '@/components/bundle/DeleteAlertDialog';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import { Loading } from '@/components/shared';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/NewButton';
import { type BundleListItem, useBundles } from '@/hooks/useBundles';
import { type BundleBase, bundlePaths } from '@/lib/bundleRoutes';

const formatDateTime = (value?: string | null, opts?: { includeTime?: boolean }) => {
  if (!value) return '-';
  const d = new Date(value);
  if (opts?.includeTime) {
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }
  return d.toLocaleDateString();
};

const Bundles = ({ slug, base }: { slug: string; base: BundleBase }) => {
  const { selectedCase } = useCaseContext();
  const navigate = useNavigate();
  const paths = bundlePaths(base);
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const breadcrumbSubheader = React.useMemo(() => (
    <span className="flex items-center text-gray-500">
      <Link to="/matter/$slug/case-files" params={{ slug }} className="hover:text-gray-700 font-medium">
        Case Files
      </Link>
      <span className="flex items-center">
        <ChevronRight className="h-4 w-4 mx-1" />
        <span className="hover:text-gray-700 font-medium">{ui_labels.bundle_plural}</span>
      </span>
    </span>
  ), [slug, ui_labels.bundle_plural]);

  useShellHeader({
    header: ui_labels.bundle_plural,
    subheader: breadcrumbSubheader,
  });

  const { bundles, isLoading, deleteBundle } = useBundles(slug);

  const [bundleToDelete, setBundleToDelete] = React.useState<BundleListItem | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = React.useState(false);
  const [sortMode, setSortMode] = React.useState<
    'updated_desc' | 'updated_asc' | 'title_asc' | 'title_desc'
  >('updated_desc');

  const sortedBundles = React.useMemo(() => {
    return [...bundles].sort((a, b) => {
      if (sortMode === 'title_asc') {
        return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      }

      if (sortMode === 'title_desc') {
        return b.title.localeCompare(a.title, undefined, { sensitivity: 'base' });
      }

      const aDate = new Date(a.updated_at ?? a.created_at).getTime();
      const bDate = new Date(b.updated_at ?? b.created_at).getTime();
      return sortMode === 'updated_desc' ? bDate - aDate : aDate - bDate;
    });
  }, [bundles, sortMode]);

  const handleDelete = async () => {
    if (!bundleToDelete) return;
    setIsDeleting(true);
    try {
      await deleteBundle(bundleToDelete.bundle_id);
    } catch (error) {
      console.error('Failed to delete bundle:', error);
    } finally {
      setIsDeleting(false);
      setBundleToDelete(null);
    }
  };

  // Rendered in the shell header (below the title) via useShellHeaderBelow, not inline.
  const bundlesHeaderBelow = React.useMemo(() => (
    <div className="flex items-end gap-4 pb-2">
      {bundles.length > 0 && (
        <div className="flex items-center gap-2">
          <label htmlFor="bundle-sort" className="text-sm text-muted-foreground">
            Sort by
          </label>
          <select
            id="bundle-sort"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
            value={sortMode}
            onChange={(e) =>
              setSortMode(
                e.target.value as
                  | 'updated_desc'
                  | 'updated_asc'
                  | 'title_asc'
                  | 'title_desc',
              )
            }
          >
            <option value="updated_desc">Updated (Newest first)</option>
            <option value="updated_asc">Updated (Oldest first)</option>
            <option value="title_asc">Alphabetical (A-Z)</option>
            <option value="title_desc">Alphabetical (Z-A)</option>
          </select>
        </div>
      )}
      <div className="ml-auto">
        <Button
          onClick={() => setIsCreateDrawerOpen(true)}
          text={ui_labels.bundle_create_title}
          icon={Plus}
        />
      </div>
    </div>
  ), [bundles.length, sortMode, ui_labels.bundle_create_title]);

  useShellHeaderBelow(selectedCase ? bundlesHeaderBelow : null);

  if (!selectedCase) {
    return <NoCaseSelected />;
  }

  return (
    <div className="mx-auto flex w-full max-w-(--breakpoint-xl) flex-col pt-6 pl-2 pr-4">
      <div className="flex flex-row gap-6">
        <div className="w-full px-1">
          {isLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Loading />
            </div>
          ) : (
            <>
              <div
                className={
                  bundles.length === 0
                    ? 'flex min-h-[min(36rem,calc(100dvh-14rem))] items-center justify-center py-10'
                    : 'mb-12 mt-2'
                }
              >
                <BundlesCasePartnerComposer
                  matterGuid={slug}
                  existingBundleTitles={sortedBundles.map((bundle) => bundle.title)}
                />
              </div>
              {bundles.length > 0 && (
              <>
              <div className="rounded-md">
                <div className="space-y-3">
                {sortedBundles.map((bundle) => {
                  const lastChangedLabel = bundle.updated_at
                    ? `Updated ${formatDateTime(bundle.updated_at, { includeTime: true })}`
                    : `Created ${formatDateTime(bundle.created_at, { includeTime: true })}`;
                  const lastActorName = bundle.updated_at
                    ? (bundle.updated_by_name ?? bundle.created_by_name)
                    : bundle.created_by_name;

                  return (
                    <div
                      key={bundle.bundle_id}
                      className="flex items-center rounded-md border bg-white p-4 hover:bg-secondary/50 transition-colors"
                    >
                      <div
                        className="grid min-w-0 flex-1 cursor-pointer grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] items-start gap-x-4 gap-y-1"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate({ to: paths.detail, params: { slug, bundleId: bundle.bundle_id } })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate({ to: paths.detail, params: { slug, bundleId: bundle.bundle_id } });
                          }
                        }}
                      >
                        <p className="col-start-1 row-start-1 min-w-0 font-medium text-foreground">
                          {bundle.title}
                        </p>
                        <p className="col-start-1 row-start-2 min-w-0 max-w-[760px] self-end truncate text-sm text-muted-foreground">
                          {bundle.description || 'No description'}
                        </p>
                        <div className="col-start-2 row-start-1 flex max-w-full shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs text-muted-foreground sm:gap-x-4 sm:whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5" />
                            {bundle.file_count} file{bundle.file_count !== 1 ? 's' : ''}
                          </span>
                          <span className="hidden items-center gap-1.5 sm:inline-flex">
                            <FolderOpen className="h-3.5 w-3.5" />
                            {bundle.section_count} section{bundle.section_count !== 1 ? 's' : ''}
                          </span>
                          <span className="hidden items-center gap-1.5 sm:inline-flex">
                            <BookOpenText className="h-3.5 w-3.5" />
                            {bundle.volume_count} volume{bundle.volume_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="col-start-2 row-start-2 flex shrink-0 items-center justify-end gap-2 self-end text-xs leading-none text-muted-foreground">
                          <span className="shrink-0 whitespace-nowrap">{lastChangedLabel}</span>
                          {lastActorName ? (
                            <span className="hidden min-w-0 max-w-48 items-center gap-1 sm:inline-flex">
                              <User className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              <span className="truncate">{lastActorName}</span>
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="ml-4 inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent hover:text-accent-foreground"
                            aria-label="Open menu"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            onClick={() => navigate({ to: paths.detail, params: { slug, bundleId: bundle.bundle_id } })}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => navigate({ to: paths.edit, params: { slug, bundleId: bundle.bundle_id } })}
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onClick={() => setBundleToDelete(bundle)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
                </div>
              </div>
              <div className="mt-2 px-2 text-sm text-muted-foreground">
                {sortedBundles.length} {sortedBundles.length === 1 ? ui_labels.bundle_singular.toLowerCase() : ui_labels.bundle_plural.toLowerCase()}
              </div>
              </>
              )}
            </>
          )}
        </div>
      </div>

      <DeleteAlertDialog
        open={!!bundleToDelete}
        onOpenChange={(open) => {
          if (!open) setBundleToDelete(null);
        }}
        title={bundleToDelete?.title ?? ''}
        isDeleting={isDeleting}
        onDelete={handleDelete}
      />

      <CreateBundleDrawer
        open={isCreateDrawerOpen}
        onOpenChange={setIsCreateDrawerOpen}
        matterGuid={slug}
        onSuccess={(bundleId) => {
          navigate({ to: paths.edit, params: { slug, bundleId } });
        }}
      />
    </div>
  );
};
// `binders` is the US-facing alias for `bundles`; both routes render the same page.
// Each route gets its own wrapper so params are read with a concrete, typed `from`
// (no `strict: false` + cast needed).
export const BundlesIndexPage = () => {
  const { slug } = useParams({ from: '/matter/$slug/bundles' });
  return <Bundles slug={slug} base="bundles" />;
};

export const BindersIndexPage = () => {
  const { slug } = useParams({ from: '/matter/$slug/binders' });
  return <Bundles slug={slug} base="binders" />;
};

export default BundlesIndexPage;
